import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import { Box, Button, FontAwesomeIcon } from "@jewlr/storybook/core"
import loadable from "@loadable/component"
import PropTypes from "prop-types"
import React, { useEffect, useState, Suspense } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import withImages from "areas/product/hoc/with-images"
import IntersectionWrapper from "components/common/intersection-wrapper"
import Slider from "dependencies/carousel"
import { retry } from "helpers/application"
import { isVideoView, isJ3dView } from "helpers/gallery-slide-view"
import useWindowDimensions from "helpers/window-dimensions"

import { StyledImage } from "./components"
import MagnifyImage from "./magnify-image"
import ProductVideo from "./product-video"

const ProductJ3D = loadable(() => retry(() => import("./product-j3d")), {
  ssr: false,
})
const ProductJ3DV2 = loadable(() => retry(() => import("./product-j3d-v2")), {
  ssr: false,
})

const ImageSlider = styled(Slider)`
  .slider-list {
    gap: 1px;
  }
  .slider-control-bottomcenter {
    margin-bottom: -32px;
  }
  .slider-control-bottomcenter button {
    padding: 0 4px;
  }
  .slider-control-bottomcenter button svg {
    height: 8px;
    width: 8px;
  }

  ${(props) => props.theme.mediaQueries.desktop`
    padding-bottom: 5px;

    .slider-control-bottomcenter {
      display: none;
    }
  `}
`

const SpinnerContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  position: absolute;
  width: 100%;
  z-index: 1;
`
const TangibleeBtn = styled(Button)`
  border-radius: 4px;
  bottom: 10px;
  display: none;
  font-size: 14px;
  left: 50%;
  padding: 16px 24px;
  position: absolute;
  transform: translateX(-50%);
  width: 90%;
  ${(props) => props.theme.mediaQueries.tablet`
    left: 0;
    width: 100%;
    transform: none;
  `}
`

const MainImageSlider = ({
  currentSlideIndex,
  dataCyType,
  defaultImage,
  enableJ3D,
  j3dSlideIndex,
  productTitle,
  showJ3d,
  showJ3dV2,
  slidesArray,
  trackSlide,
  wizardLoading,
  zoomImg,
}) => {
  const [blur, setBlur] = useState(!!wizardLoading)
  const { isTablet } = useWindowDimensions()

  const swipeable =
    !sl.config.enable_j3d_carousel ||
    (j3dSlideIndex > 0 && slidesArray.length > 1
      ? currentSlideIndex < slidesArray.length - 1
      : true)

  const sliderSettings = {
    arrows: false,
    beforeSlide: (_, index) => trackSlide(index),
    dragging: swipeable,
    frameAriaLabel: "Product Images",
    slideIndex: currentSlideIndex,
    speed: 300,
    swiping: swipeable,
    tabbed: isTablet,
    wrapAround: false,
  }

  useEffect(() => {
    // Unset and blur image when wizard is loading, so that old image does not remain on page
    if (wizardLoading) {
      setBlur(true)
    }
  }, [wizardLoading])

  return (
    <>
      {(wizardLoading || blur) && (
        <SpinnerContainer data-cy="product-main-slider-main-img-spinning">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin />
        </SpinnerContainer>
      )}
      <Box position="relative">
        <ImageSlider {...sliderSettings}>
          {!defaultImage && slidesArray.length == 0 && (
            <Box position="relative">
              <StyledImage
                alt="Loading"
                blur
                ratio="325 / 325"
                src={require("images/icons/jewlr.gif")}
              />
            </Box>
          )}
          {slidesArray.map((slide, index) => (
            <IntersectionWrapper
              aria-labelledby={`image-slider-tab-${index + 1}`}
              disabled={index === 0}
              height="100%"
              id={`image-slider-tabpanel-${index + 1}-main`}
              isImg={!isVideoView(slide.view) && !isJ3dView(slide.view)}
              key={`mainslider-${slide.view}`}
              role="tabpanel"
              tabIndex={currentSlideIndex === index ? "0" : "-1"}
              width="100%"
            >
              {isVideoView(slide.view) ? (
                <ProductVideo
                  video={slide.src}
                  {...{ currentSlideIndex, index }}
                />
              ) : isJ3dView(slide.view) ? (
                <Box
                  height="100%"
                  maxHeight={{
                    _: "100%",
                    desktop: "500px",
                    tablet: "325px",
                  }}
                  maxWidth={{
                    _: "100%",
                    desktop: "500px",
                    tablet: "325px",
                  }}
                  mx="auto"
                  width="100%"
                >
                  {typeof window !== "undefined" && enableJ3D && (
                    <Suspense fallback={<div />}>
                      {showJ3d && !showJ3dV2 && <ProductJ3D />}
                      {showJ3dV2 && <ProductJ3DV2 />}
                    </Suspense>
                  )}
                </Box>
              ) : (
                <Box position="relative">
                  {sl.config.enable_pdp_image_zoom ? (
                    <MagnifyImage
                      alt={productTitle}
                      blur={blur || wizardLoading}
                      className={`prod-img-${slide.view}`}
                      data-cy={`product-main-slider-main-img-${index}${
                        dataCyType ? `-${dataCyType}` : ""
                      }`}
                      lazyload={index > 0}
                      mgShowZoom={
                        sl.config.enable_pdp_image_zoom &&
                        currentSlideIndex === index &&
                        zoomImg &&
                        !isVideoView(slide.view) &&
                        !isJ3dView(slide.view)
                      }
                      onLoad={() => {
                        if (index == 0) {
                          // setShowSrc(true)
                          setBlur(false)
                        }
                      }}
                      ratio="325 / 325"
                      sizes={slide.sizes}
                      src={slide.src}
                      srcSet={slide.srcset}
                    />
                  ) : (
                    <StyledImage
                      alt={productTitle}
                      blur={blur || wizardLoading}
                      className={`prod-img-${slide.view}`}
                      data-cy={`product-main-slider-main-img-${index}${
                        dataCyType ? `-${dataCyType}` : ""
                      }`}
                      lazyload={index > 0}
                      onLoad={() => {
                        if (index == 0) {
                          // setShowSrc(true)
                          setBlur(false)
                        }
                      }}
                      ratio="325 / 325"
                      sizes={slide.sizes}
                      src={slide.src}
                      srcSet={slide.srcset}
                    />
                  )}

                  {slide.view === "tangiblee_view" && (
                    <TangibleeBtn
                      className="js-tangiblee-cta"
                      variant="primary"
                    >
                      TAP TO VIEW SIZE
                    </TangibleeBtn>
                  )}
                </Box>
              )}
            </IntersectionWrapper>
          ))}
        </ImageSlider>
      </Box>
    </>
  )
}

MainImageSlider.propTypes = {
  currentSlideIndex: PropTypes.number,
  dataCyType: PropTypes.string,
  defaultImage: PropTypes.object,
  enableJ3D: PropTypes.bool,
  j3dSlideIndex: PropTypes.number,
  productTitle: PropTypes.string,
  showJ3d: PropTypes.bool,
  showJ3dV2: PropTypes.bool,
  slidesArray: PropTypes.array,
  styleCode: PropTypes.string,
  trackSlide: PropTypes.func,
  wizardLoading: PropTypes.string,
  zoomImg: PropTypes.object,
}

const mapStateToProps = (state) => ({
  showJ3d: state.previewImage.j3d,
  showJ3dV2: state.previewImage.j3dV2,
})

export default withImages(connect(mapStateToProps)(MainImageSlider))
